import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// ─── localStorage helpers for member token ─────────────────────────────────
const MEMBER_TOKEN_KEY = 'memberToken';
const MEMBER_KEY = 'memberInfo';

function getMemberToken() { return localStorage.getItem(MEMBER_TOKEN_KEY); }
function setMemberToken(token) { localStorage.setItem(MEMBER_TOKEN_KEY, token); }
function getMemberInfo() {
  try { return JSON.parse(localStorage.getItem(MEMBER_KEY)); } catch { return null; }
}
function setMemberInfo(info) { localStorage.setItem(MEMBER_KEY, JSON.stringify(info)); }
function clearMemberSession() { localStorage.removeItem(MEMBER_TOKEN_KEY); localStorage.removeItem(MEMBER_KEY); }

function memberHeaders() {
  const token = getMemberToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Shared styles ──────────────────────────────────────────────────────────
const pageStyle = { minHeight: '100vh', backgroundColor: '#1a1a2e', color: '#ffffff', padding: '2rem 1rem' };
const cardStyle = { maxWidth: 420, margin: '0 auto', backgroundColor: '#16213e', borderRadius: 12, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #333', backgroundColor: '#0f0f1a', color: '#fff', fontSize: '0.95rem', marginBottom: 12, boxSizing: 'border-box' };
const btnPrimary = { width: '100%', padding: '12px', borderRadius: 6, border: 'none', backgroundColor: '#ff6800', color: '#fff', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' };
const btnOutline = { ...btnPrimary, backgroundColor: 'transparent', border: '1px solid #ff6800', color: '#ff6800' };

// ─── Stripe PaymentForm (rendered inside <Elements>) ────────────────────────
function PaymentForm({ onSuccess, amount }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message);
      setProcessing(false);
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setError('Payment did not complete. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && <p style={{ color: '#ef4444', marginTop: 8, fontSize: '0.85rem' }}>{error}</p>}
      <button
        type="submit"
        disabled={!stripe || processing}
        style={{ ...btnPrimary, marginTop: 16, opacity: processing ? 0.7 : 1 }}
      >
        {processing ? 'Processing...' : `Pay $${Number(amount).toFixed(2)}`}
      </button>
    </form>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function PublicEventHistory() {
  const { tenant } = useParams();
  const tenantNum = Number(tenant) || null;

  // Data states
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tenantName, setTenantName] = useState('');

  // Access gate states
  const [requireLogin, setRequireLogin] = useState(false);
  const [accessFee, setAccessFee] = useState(0);
  const [gate, setGate] = useState('loading'); // loading | open | login | setup-password | payment | ready
  const [member, setMember] = useState(getMemberInfo());

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);
  const [, setNeedsPasswordSetup] = useState(false);

  // Password setup form
  const [setupFirstname, setSetupFirstname] = useState('');
  const [setupLastname, setSetupLastname] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [setupConfirm, setSetupConfirm] = useState('');
  const [setupError, setSetupError] = useState(null);
  const [setupBusy, setSetupBusy] = useState(false);

  // Payment
  const [clientSecret, setClientSecret] = useState(null);
  const [, setPaymentIntentId] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);

  // Venmo
  const [venmoConfig, setVenmoConfig] = useState(null);

  // ── Step 1: fetch tenant config ─────────────────────────────────────────
  useEffect(() => {
    if (!tenantNum) return;
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/events/${tenantNum}/publicHistoryConfig`);
        if (res.data?.success) {
          setTenantName(res.data.tenantName || '');
          setRequireLogin(res.data.requireLogin);
          setAccessFee(res.data.accessFee || 0);

          if (!res.data.requireLogin) {
            setGate('open');
          } else if (getMemberToken()) {
            // Already have a token — check access
            setGate('ready');
          } else {
            setGate('login');
          }
        } else {
          setErr(res.data?.message || 'Event history is not available.');
          setGate('open');
        }
      } catch (e) {
        if (e.response?.status === 403) {
          setErr('This event history is not publicly available.');
        } else {
          setErr('Failed to load event history.');
        }
        setGate('open');
      }
    })();
  }, [tenantNum]);

  // ── Fetch Venmo config for event button ─────────────────────────────────
  useEffect(() => {
    if (!tenantNum) return;
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/venmo/public/${tenantNum}`);
        if (res.data?.success && res.data?.enabled) {
          setVenmoConfig(res.data.venmo);
        }
      } catch {
        // Venmo not configured — that's fine
      }
    })();
  }, [tenantNum]);

  // ── Step 2: fetch history data when gate is open/ready ──────────────────
  const fetchHistory = useCallback(async () => {
    if (!tenantNum) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${baseUrl}/api/events/${tenantNum}/publicHistory`, {
        headers: memberHeaders(),
      });
      if (res.data?.success) {
        setItems(res.data.items || []);
        setTenantName(res.data.tenantName || '');
      } else {
        setErr(res.data?.message || 'Event history is not available.');
      }
    } catch (e) {
      const status = e.response?.status;
      const data = e.response?.data;
      if (status === 401 && data?.requireLogin) {
        clearMemberSession();
        setMember(null);
        setGate('login');
      } else if (status === 402 && data?.requirePayment) {
        setAccessFee(data.fee);
        setGate('payment');
      } else if (status === 403) {
        setErr('This event history is not publicly available.');
      } else {
        setErr('Failed to load event history.');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantNum]);

  useEffect(() => {
    if (gate === 'open' || gate === 'ready') {
      fetchHistory();
    }
  }, [gate, fetchHistory]);

  // ── Login handler ───────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError(null);
    try {
      const res = await axios.post(`${baseUrl}/api/members/${tenantNum}/member-login`, {
        email: loginEmail,
        password: loginPassword,
      });
      if (res.data?.success) {
        setMemberToken(res.data.token);
        setMemberInfo(res.data.member);
        setMember(res.data.member);
        setGate('ready');
      }
    } catch (e) {
      if (e.response?.data?.needsPasswordSetup) {
        setNeedsPasswordSetup(true);
        setGate('setup-password');
      } else {
        setLoginError(e.response?.data?.message || 'Login failed');
      }
    } finally {
      setLoginBusy(false);
    }
  };

  // ── Password setup handler ──────────────────────────────────────────────
  const handleSetupPassword = async (e) => {
    e.preventDefault();
    if (setupPassword !== setupConfirm) {
      setSetupError('Passwords do not match');
      return;
    }
    setSetupBusy(true);
    setSetupError(null);
    try {
      const res = await axios.post(`${baseUrl}/api/members/${tenantNum}/member-set-password`, {
        email: loginEmail,
        firstname: setupFirstname,
        lastname: setupLastname,
        password: setupPassword,
      });
      if (res.data?.success) {
        setMemberToken(res.data.token);
        setMemberInfo(res.data.member);
        setMember(res.data.member);
        setGate('ready');
      }
    } catch (e) {
      setSetupError(e.response?.data?.message || 'Failed to set password');
    } finally {
      setSetupBusy(false);
    }
  };

  // ── Payment initiation ─────────────────────────────────────────────────
  useEffect(() => {
    if (gate !== 'payment' || clientSecret) return;
    (async () => {
      setPaymentBusy(true);
      try {
        const res = await axios.post(
          `${baseUrl}/api/members/${tenantNum}/member-pay-access`,
          {},
          { headers: memberHeaders() }
        );
        if (res.data?.alreadyPaid) {
          setGate('ready');
        } else if (res.data?.clientSecret) {
          setClientSecret(res.data.clientSecret);
          setPaymentIntentId(res.data.paymentIntentId);
        }
      } catch (e) {
        setErr(e.response?.data?.message || 'Payment setup failed');
        setGate('open');
      } finally {
        setPaymentBusy(false);
      }
    })();
  }, [gate, clientSecret, tenantNum]);

  // ── Payment confirmation ────────────────────────────────────────────────
  const handlePaymentSuccess = async (piId) => {
    try {
      await axios.post(
        `${baseUrl}/api/members/${tenantNum}/member-confirm-payment`,
        { paymentIntentId: piId },
        { headers: memberHeaders() }
      );
      setGate('ready');
    } catch {
      setErr('Payment recorded but confirmation failed. Please refresh.');
    }
  };

  // ── Logout ──────────────────────────────────────────────────────────────
  const handleLogout = () => {
    clearMemberSession();
    setMember(null);
    setItems([]);
    setGate('login');
  };

  // ── Group items by date ─────────────────────────────────────────────────
  const groups = useMemo(() => {
    const by = {};
    for (const it of items) {
      const d = new Date(it.createdOn);
      if (!Number.isNaN(d.getTime())) {
        const key = d.toISOString().slice(0, 10);
        (by[key] ||= []).push(it);
      }
    }
    return Object.entries(by).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [items]);

  // ─── RENDER ─────────────────────────────────────────────────────────────

  // Login form
  if (gate === 'login') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2 style={{ textAlign: 'center', color: '#ff6800', marginBottom: '1.5rem' }}>
            {tenantName ? `${tenantName}` : 'Event History'}
          </h2>
          <p style={{ textAlign: 'center', color: '#aaa', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Sign in to view event history
          </p>
          <form onSubmit={handleLogin}>
            <input
              type="email"
              placeholder="Email address"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
              style={inputStyle}
            />
            {loginError && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 8 }}>{loginError}</p>
            )}
            <button type="submit" disabled={loginBusy} style={{ ...btnPrimary, opacity: loginBusy ? 0.7 : 1 }}>
              {loginBusy ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          <p style={{ textAlign: 'center', color: '#aaa', marginTop: '1rem', fontSize: '0.85rem' }}>
            First time?{' '}
            <button
              type="button"
              onClick={() => setGate('setup-password')}
              style={{ background: 'none', border: 'none', color: '#ff6800', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}
            >
              Set up your password
            </button>
          </p>
          <PoweredByFooter />
        </div>
      </div>
    );
  }

  // Password setup form
  if (gate === 'setup-password') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2 style={{ textAlign: 'center', color: '#ff6800', marginBottom: '0.5rem' }}>
            Set Your Password
          </h2>
          <p style={{ textAlign: 'center', color: '#aaa', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
            Enter the email and name you registered with, then choose a password.
          </p>
          <form onSubmit={handleSetupPassword}>
            <input
              type="email"
              placeholder="Email address"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="First name"
              value={setupFirstname}
              onChange={(e) => setSetupFirstname(e.target.value)}
              required
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Last name"
              value={setupLastname}
              onChange={(e) => setSetupLastname(e.target.value)}
              required
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="New password (min 8 characters)"
              value={setupPassword}
              onChange={(e) => setSetupPassword(e.target.value)}
              required
              minLength={8}
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Confirm password"
              value={setupConfirm}
              onChange={(e) => setSetupConfirm(e.target.value)}
              required
              style={inputStyle}
            />
            {setupError && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 8 }}>{setupError}</p>
            )}
            <button type="submit" disabled={setupBusy} style={{ ...btnPrimary, opacity: setupBusy ? 0.7 : 1 }}>
              {setupBusy ? 'Setting password...' : 'Set Password & Sign In'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => setGate('login')}
              style={{ background: 'none', border: 'none', color: '#ff6800', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}
            >
              Back to sign in
            </button>
          </p>
          <PoweredByFooter />
        </div>
      </div>
    );
  }

  // Payment gate
  if (gate === 'payment') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2 style={{ textAlign: 'center', color: '#ff6800', marginBottom: '0.5rem' }}>
            {tenantName ? `${tenantName}` : 'Event History'}
          </h2>
          <p style={{ textAlign: 'center', color: '#aaa', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Access to event history requires a one-time payment of <strong style={{ color: '#fff' }}>${Number(accessFee).toFixed(2)}</strong>
          </p>
          {member && (
            <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.85rem', marginBottom: '1rem' }}>
              Signed in as {member.email}
            </p>
          )}
          {paymentBusy && (
            <p style={{ textAlign: 'center', color: '#aaa' }}>Setting up payment...</p>
          )}
          {clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night', variables: { colorPrimary: '#ff6800' } } }}>
              <PaymentForm onSuccess={handlePaymentSuccess} amount={accessFee} />
            </Elements>
          )}
          {!stripePromise && !paymentBusy && (
            <p style={{ textAlign: 'center', color: '#ef4444', fontSize: '0.85rem' }}>
              Payment is not configured. Please contact the event organizer.
            </p>
          )}
          <PoweredByFooter />
        </div>
      </div>
    );
  }

  // Loading gate check
  if (gate === 'loading') {
    return (
      <div style={pageStyle}>
        <p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p>
      </div>
    );
  }

  // ── Main history view (gate === 'open' or 'ready') ────────────────────
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header with optional logout */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '2rem', position: 'relative' }}>
          <h2 style={{ textAlign: 'center', color: '#ff6800', margin: 0 }}>
            {tenantName ? `${tenantName} — Event History` : 'Event History'}
          </h2>
          {member && requireLogin && (
            <button
              type="button"
              onClick={handleLogout}
              style={{
                ...btnOutline,
                width: 'auto',
                padding: '6px 14px',
                fontSize: '0.8rem',
                position: 'absolute',
                right: 0,
              }}
            >
              Sign Out
            </button>
          )}
        </div>

        {loading && (
          <p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p>
        )}

        {err && (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            backgroundColor: '#16213e',
            borderRadius: 8,
            color: '#ff6800',
          }}>
            {err}
          </div>
        )}

        {!loading && !err && items.length === 0 && (
          <p style={{ textAlign: 'center', color: '#aaa' }}>No event history yet.</p>
        )}

        {groups.map(([date, arr]) => (
          <div key={date} style={{ marginBottom: '2rem' }}>
            <h4 style={{ color: '#ff6800', marginBottom: '1rem', borderBottom: '1px solid #333', paddingBottom: '0.5rem' }}>
              {new Date(date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '1rem',
            }}>
              {arr.map((it) => (
                <div
                  key={`${it.kind}-${it.fileId}`}
                  style={{
                    backgroundColor: '#16213e',
                    borderRadius: 8,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  <div style={{
                    aspectRatio: '4/3',
                    backgroundColor: '#0f0f1a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {it.mimetype?.startsWith('video/') ? (
                      <video
                        src={it.url}
                        controls
                        playsInline
                        preload="metadata"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <img
                        src={it.url}
                        alt={it.title || 'event image'}
                        loading="lazy"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                  </div>
                  <div style={{ padding: '0.75rem' }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.title}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '0.25rem' }}>
                      {it.kind === 'item' ? `Event Item` : `Event`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {venmoConfig && (
          <VenmoButton venmo={venmoConfig} />
        )}

        <PoweredByFooter />
      </div>
    </div>
  );
}

function VenmoButton({ venmo }) {
  const venmoUrl = `https://venmo.com/${venmo.username.replace(/^@/, '')}` +
    (venmo.defaultAmount ? `?txn=pay&amount=${venmo.defaultAmount}` : '?txn=pay') +
    (venmo.defaultNote ? `&note=${encodeURIComponent(venmo.defaultNote)}` : '');

  return (
    <div style={{
      textAlign: 'center',
      margin: '2rem auto',
      padding: '1.5rem',
      backgroundColor: '#16213e',
      borderRadius: 12,
      maxWidth: 400,
    }}>
      <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '1rem' }}>
        {venmo.defaultNote || `Pay ${venmo.displayName}`}
      </p>
      <a
        href={venmoUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 28px',
          backgroundColor: '#008CFF',
          color: '#fff',
          fontWeight: 700,
          fontSize: '1rem',
          borderRadius: 8,
          textDecoration: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff">
          <path d="M19.135 1.755c.8 1.32 1.16 2.68 1.16 4.4 0 5.48-4.68 12.6-8.48 17.6H4.8L2.16 2.395l6.24-.6 1.56 12.52c1.44-2.36 3.24-6.08 3.24-8.64 0-1.64-.28-2.76-.72-3.68l6.655-.245z"/>
        </svg>
        Pay with Venmo
        {venmo.defaultAmount ? ` — $${Number(venmo.defaultAmount).toFixed(2)}` : ''}
      </a>
      <p style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.75rem' }}>
        Opens Venmo to pay {venmo.displayName}
      </p>
    </div>
  );
}

function PoweredByFooter() {
  return (
    <div style={{
      textAlign: 'center',
      marginTop: '3rem',
      paddingTop: '1.5rem',
      borderTop: '1px solid #333',
      color: '#666',
      fontSize: '0.85rem',
    }}>
      Powered by{' '}
      <a
        href="https://on-screen.app"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#ff6800', textDecoration: 'none' }}
      >
        On-Screen
      </a>
    </div>
  );
}
